import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import { emptyDisplayValue } from 'constants/common';
import {
    ContentRoleLevel,
    Grade,
    SoftSkillRoleLevelContent,
    useSaveSoftSkillContentMutation,
    useSoftSkillRoleLevelContentFindManyAndCountQuery,
} from 'generated/graphql';
import { useState } from 'react';
import SoftSkillTabSection from 'views/StaticContent/SoftSkills/drawers/children/SoftSkillTabSection';
import {
    Button,
    Loader,
    Tag,
    TagColor,
    TextInput,
    TNotification,
    useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
    getContent,
    getInitialValues,
    GRADES,
    IMMUTABLE_FIELDS,
    ROLE_LEVELS,
} from './SoftSkillsContent.constants';
import styles from './SoftSkillsContent.module.scss';

const SoftSkillsContent = ({ softSkillId }: { softSkillId: number }) => {
    const { handleMsgType } = useNotification();
    const [filters, setFilters] = useState<{
        grades: Grade[];
        roleLevels: ContentRoleLevel[];
    }>({
        grades: [],
        roleLevels: [],
    });

    const { data, loading } = useSoftSkillRoleLevelContentFindManyAndCountQuery({
        variables: {
            softSkillIds: [softSkillId],
            grades: filters.grades,
            roleLevels: filters.roleLevels,
            take: 100,
            page: 1,
        },
        onError: error => {
            handleMsgType({
                type: TNotification.error,
                dismissable: true,
                title: 'Failed to load soft skill content',
                message: error.message,
            });
        },
    });

    const handleGradeClick = (grade: Grade) => {
        if (filters.grades.includes(grade)) {
            setFilters({
                ...filters,
                grades: filters.grades.filter(g => g !== grade),
            });
        } else {
            setFilters({ ...filters, grades: [...filters.grades, grade] });
        }
    };

    const handleRoleLevelClick = (roleLevel: ContentRoleLevel) => {
        if (filters.roleLevels.includes(roleLevel)) {
            setFilters({
                ...filters,
                roleLevels: filters.roleLevels.filter(rl => rl !== roleLevel),
            });
        } else {
            setFilters({
                ...filters,
                roleLevels: [...filters.roleLevels, roleLevel],
            });
        }
    };

    return (
        <SoftSkillTabSection
            title="Content"
            description="Content that appear in Hiring manager or Candidate reports, based on the skill's grade and role level."
            hasNoItems={false}
            noItemLabel="reports content"
        >
            <div className={styles.container}>
                <div className={styles.quickFilters}>
                    Grades:
                    <div className={styles.filtersContainer}>
                        {GRADES.map(grade => (
                            <Tag
                                key={grade.value}
                                isDismissible={false}
                                onClick={() => handleGradeClick(grade.value)}
                                tagColor={
                                    filters.grades.includes(grade.value)
                                        ? TagColor.INTERACTIVE
                                        : TagColor.BLUE
                                }
                            >
                                {grade.label}
                            </Tag>
                        ))}
                    </div>
                </div>
                <div className={styles.quickFilters}>
                    Role levels:
                    <div className={styles.filtersContainer}>
                        {ROLE_LEVELS.map(roleLevel => (
                            <Tag
                                key={roleLevel.value}
                                isDismissible={false}
                                onClick={() => handleRoleLevelClick(roleLevel.value)}
                                tagColor={
                                    filters.roleLevels.includes(roleLevel.value)
                                        ? TagColor.INTERACTIVE
                                        : TagColor.BLUE
                                }
                            >
                                {roleLevel.label}
                            </Tag>
                        ))}
                    </div>
                </div>
                <div className={styles.cardsContainer}>
                    {loading ? (
                        <div className={styles.loaderContainer}>
                            <Loader />
                        </div>
                    ) : (
                        data?.softSkillContent?.softSkillContent?.map(content => (
                            <SoftSkillContentCard
                                id={content.id}
                                key={content.id}
                                content={getContent(content as SoftSkillRoleLevelContent)}
                            />
                        ))
                    )}
                </div>
            </div>
        </SoftSkillTabSection>
    );
};

export default SoftSkillsContent;

const SoftSkillContentCard = ({
    content,
    id,
    isSkeleton = false,
}: {
    content: { label: string; value?: string | null; key: string }[];
    id: number;
    isSkeleton?: boolean;
}) => {
    const [isEditMode, setIsEditMode] = useState(false);
    const [values, setValues] = useState<{
        description?: string | null;
        hiringManagerContent?: string | null;
        candidateContent?: string | null;
        internalCandidateContent?: string | null;
    }>(getInitialValues(content));
    const { handleMsgType } = useNotification();

    const [saveSoftSkillContent, { loading }] = useSaveSoftSkillContentMutation({
        onError: error => {
            handleMsgType({
                type: TNotification.error,
                dismissable: true,
                title: 'Failed to save soft skill content',
                message: error.message,
            });
        },
        onCompleted: (data) => {
            setValues({
                description: data.saveSoftSkillContent?.description,
                hiringManagerContent: data.saveSoftSkillContent?.hiringManagerContent,
                candidateContent: data.saveSoftSkillContent?.candidateContent,
                internalCandidateContent: data.saveSoftSkillContent?.internalCandidateContent,
            });
            setIsEditMode(false);
            handleMsgType({
                type: TNotification.success,
                dismissable: true,
                title: 'Updated soft skill content successfully',
            });
        },
    });

    const handleSave = () => {
        const description = values.description?.trim();
        const hiringManagerContent = values.hiringManagerContent?.trim();
        const candidateContent = values.candidateContent?.trim();
        const internalCandidateContent = values.internalCandidateContent?.trim();
        saveSoftSkillContent({
            variables: {
                id,
                description,
                hiringManagerContent,
                candidateContent,
                internalCandidateContent,
            },
        });
    };
    const handleCancel = () => {
        setValues(getInitialValues(content));
        setIsEditMode(false);
    };

    const handleEdit = () => {
        setIsEditMode(true);
    };

    const handleChangeValue = (id: string, value: string) => {
        setValues({ ...values, [id]: value });
    };

    return (
        <div className={`${styles.contentCard} ${isSkeleton ? styles.skeleton : ''}`}>
            <div className={styles.actionButtonsContainer}>
                {isEditMode && (
                    <Button
                        variant="secondary"
                        size="small"
                        className={styles.editContentButton}
                        onClick={handleCancel}
                    >
                        Cancel
                    </Button>
                )}
                {isSkeleton ? null : <Button
                    variant="primary"
                    size="small"
                    loading={loading}
                    onClick={isEditMode ? handleSave : handleEdit}
                >
                    {isEditMode ? 'Save' : 'Edit'}
                </Button>}
            </div>

            {content
                .filter(item => IMMUTABLE_FIELDS.includes(item.label))
                .map(item => (
                    <InfoItem
                        key={item.label}
                        label={item.label}
                        value={item.value}
                        className={styles.softSkillContentItem}
                    />
                ))}
            {content
                .filter(item => !IMMUTABLE_FIELDS.includes(item.label))
                .map(item => (
                    <InfoItem
                        key={item.label}
                        label={item.label}
                        value={
                            isEditMode ? (
                                <TextInput
                                    className={styles.textInput}
                                    key={item.label}
                                    id={id.toString()}
                                    value={values[item.key as keyof typeof values] || ''}
                                    onChange={(value, _) => handleChangeValue(item.key, value)}
                                />
                            ) : (
                                item.value || emptyDisplayValue
                            )
                        }
                        className={styles.softSkillContentItem}
                    />
                ))}
        </div>
    );
};